<template>
  <li class="flight-booking-card__flight" :class="{ 'flight-booking-card__flight--removable': $slots.actions, 'flight-booking-card__flight--vertical': orientation === 'vertical', 'flight-booking-card__flight--without-aircraft': orientation === 'vertical' && !aircraft }">
    <div v-if="orientation === 'vertical'" class="flight-booking-card__flight-info">
      <time class="flight-booking-card__date" :datetime="date">{{ flightDate(date) }}</time>
      <dl class="flight-booking-card__flight-meta">
        <div><dt>Duration</dt><dd class="flight-booking-card__duration" :aria-label="duration ? `Duration ${duration}` : 'Duration unavailable'">{{ duration || '—' }}</dd></div>
        <div v-if="!empty(flightNumber)"><dt>Flight number</dt><dd>{{ flightNumber }}</dd></div>
      </dl>
    </div>
    <span v-else-if="dateNearRoute" class="flight-booking-card__date-placeholder" aria-hidden="true" />
    <time v-else class="flight-booking-card__date" :datetime="date">{{ flightDate(date) }}</time>
    <div class="flight-booking-card__flight-main">
      <div class="flight-booking-card__route">
        <div class="flight-booking-card__endpoint flight-booking-card__endpoint--origin">
          <span v-if="orientation === 'horizontal'" class="flight-booking-card__airport" :class="{ 'flight-booking-card__airport--dated': dateNearRoute }" :title="origin && origin.name">
            <template v-if="dateNearRoute">
              <time class="flight-booking-card__date flight-booking-card__date--badge" :datetime="date">{{ flightDate(date) }}</time>
              <span>{{ airport(origin) }}</span>
            </template>
            <template v-else>{{ airport(origin) }}</template>
          </span>
          <time class="flight-booking-card__time" :datetime="departure" :aria-label="`Departure ${flightTime(departure)}`">{{ flightTime(departure) }}</time>
          <span v-if="orientation === 'vertical'" class="flight-booking-card__airport" :title="origin && origin.name">{{ airport(origin) }}</span>
        </div>
        <span class="flight-booking-card__connector" aria-hidden="true" />
        <div v-if="orientation === 'vertical' && viaLabel" class="flight-booking-card__via"><span>via</span><span>{{ viaLabel }}</span></div>
        <div class="flight-booking-card__endpoint">
          <time class="flight-booking-card__time" :datetime="arrival" :aria-label="`Arrival ${flightTime(arrival)}`">{{ flightTime(arrival) }}</time>
          <span class="flight-booking-card__airport" :title="destination && destination.name">{{ airport(destination) }}</span>
        </div>
      </div>
      <div v-if="orientation === 'vertical' && showBaggage" class="flight-booking-card__baggage" aria-label="Baggage allowance">
        <i class="pi pi-briefcase" aria-hidden="true" />
        <span v-if="!empty(baggageAllowance)">Baggage <strong>{{ baggageAllowanceLabel }}</strong></span>
        <template v-if="hasInfantBaggage"><span v-if="!empty(baggageAllowance)" aria-hidden="true">·</span><span>Infant <strong>{{ infantBaggageAllowanceLabel }}</strong></span></template>
      </div>
    </div>
    <div v-if="orientation === 'vertical' && aircraft" class="flight-booking-card__aircraft">
      <span class="flight-booking-card__aircraft-label">Aircraft</span>
      <div class="flight-booking-card__aircraft-image-slot" aria-hidden="true">
        <img v-if="aircraftImage && aircraftImage !== failedImage" :src="aircraftImage" alt="" width="130" height="60" @error="failedImage = aircraftImage" />
        <span v-else class="flight-booking-card__aircraft-fallback" />
      </div>
      <span class="flight-booking-card__aircraft-name">{{ aircraft && !empty(aircraft.name) ? aircraft.name : '—' }}</span>
    </div>
    <span v-if="$slots.actions" class="flight-booking-card__flight-actions"><slot name="actions" /></span>
  </li>
</template>

<script>
import { dateTime, empty, flightDuration, formatBaggageAllowance } from '@/views/flights/bookings/booking-detail'

export default {
  name: 'FlightSegmentRow',
  props: {
    orientation: { type: String, default: 'horizontal', validator: value => ['horizontal', 'vertical'].includes(value) },
    dateNearRoute: { type: Boolean, default: false },
    date: { type: String, default: '' },
    origin: { type: Object, default: null },
    destination: { type: Object, default: null },
    departure: { type: String, default: '' },
    arrival: { type: String, default: '' },
    flightNumber: { type: String, default: null },
    via: { type: String, default: null },
    aircraft: { type: Object, default: null },
    aircraftImage: { type: String, default: '' },
    baggageAllowance: { type: [String, Number], default: null },
    infantBaggageAllowance: { type: [String, Number], default: null },
    showInfantBaggage: { type: Boolean, default: false }
  },
  data() { return { failedImage: '' } },
  computed: {
    duration() { return flightDuration(this.departure, this.arrival) },
    viaLabel() { return (this.via || '').trim() },
    hasInfantBaggage() { return this.showInfantBaggage && !empty(this.infantBaggageAllowance) },
    showBaggage() { return !empty(this.baggageAllowance) || this.hasInfantBaggage },
    baggageAllowanceLabel() { return formatBaggageAllowance(this.baggageAllowance) },
    infantBaggageAllowanceLabel() { return formatBaggageAllowance(this.infantBaggageAllowance) }
  },
  methods: {
    empty,
    flightDate(value) {
      // Flight dates are calendar dates; do not convert them through the browser's timezone.
      return value ? dateTime(`${value}T12:00:00+03:00`, true) || '—' : '—'
    },
    flightTime(value) { return value ? String(value).slice(0, 5) : '—' },
    airport(value) { return (value && value.name) || '—' }
  }
}
</script>

<style lang="scss" scoped>
.flight-booking-card__flight {
  container-type: inline-size;
  container-name: flight-segment;
  color: var(--booking-ink, #20241d);
  font: 400 15px/1.45 $font-family-base;
  display: grid;
  grid-template-columns: 130px minmax(0, 1fr);
  align-items: center;
  gap: 14px;
  min-height: 44px;
  padding: 15px 0;

  & + & { border-top: 1px solid var(--booking-line, #dce0d6); }
  &:last-child { min-height: 0; padding-bottom: 0; }
}
.flight-booking-card__date { color: var(--booking-muted, #62685d); font-variant-numeric: tabular-nums; white-space: nowrap; }
.flight-booking-card__date--badge {
  padding: 4px 7px;
  border: 1px solid var(--booking-line, #dce0d6);
  border-radius: 5px;
  background: var(--booking-green-bg, #f0f6e7);
  color: var(--booking-green, #527b12);
  font-size: 13px;
  font-weight: 500;
}
.flight-booking-card__flight-main { min-width: 0; }
.flight-booking-card__route { display: flex; align-items: center; gap: 12px; min-width: 0; }
.flight-booking-card__endpoint { display: grid; grid-template-columns: 52px minmax(0, 1fr); align-items: baseline; gap: 12px; min-width: 0; flex: 0 1 auto; }
.flight-booking-card__endpoint--origin { grid-template-columns: minmax(0, 1fr) 52px; }
.flight-booking-card__time { font-variant-numeric: tabular-nums; font-weight: 500; white-space: nowrap; }
.flight-booking-card__endpoint--origin .flight-booking-card__time { text-align: right; }
.flight-booking-card__airport { overflow-wrap: anywhere; }
.flight-booking-card__airport--dated {
  display: flex;
  align-items: baseline;
  justify-content: flex-end;
  gap: 12px;
  > * { flex-shrink: 0; }
}
.flight-booking-card__connector {
  position: relative;
  flex: 0 0 42px;
  height: 1px;
  background: var(--booking-line, #dce0d6);

  &::before,
  &::after { content: ''; position: absolute; top: -3px; width: 7px; height: 7px; border-radius: 50%; }
  &::before { left: 0; background: var(--booking-green, #527b12); }
  &::after { right: 0; background: var(--booking-muted, #62685d); }
}

.flight-booking-card__flight--removable { position: relative; padding-right: 42px; --flight-action-inset: 15px; }
.flight-booking-card__flight--removable:last-child { --flight-action-bottom: 0px; }
.flight-booking-card__flight-actions {
  position: absolute;
  right: 0;
  top: var(--flight-action-inset);
  bottom: var(--flight-action-bottom, var(--flight-action-inset));
  display: flex;
  align-items: center;
}

@container flight-segment (max-width: 800px) {
  .flight-booking-card__route {
    display: grid;
    grid-template-columns: minmax(0, 1fr) var(--flight-connector-width, 42px) minmax(0, 1fr);
    align-items: center;
  }
  .flight-booking-card__endpoint { align-items: center; }
  .flight-booking-card__endpoint:first-child .flight-booking-card__airport { text-align: right; }
  .flight-booking-card__connector { align-self: center; }
}

@media (max-width: 720px) {
  .flight-booking-card__route { --flight-connector-width: 32px; }
  .flight-booking-card__endpoint { grid-template-columns: minmax(0, 1fr); gap: 3px; }
  .flight-booking-card__airport { grid-row: 1; }
  .flight-booking-card__connector { flex-basis: 32px; }
}

@media (max-width: 520px) {
  .flight-booking-card__date-placeholder { display: none; }
  .flight-booking-card__airport--dated { flex-direction: column; align-items: flex-end; gap: 6px; }
  .flight-booking-card__flight { grid-template-columns: minmax(0, 1fr); gap: 6px; padding: 10px 0; }
  .flight-booking-card__flight--removable { padding-right: 42px; --flight-action-inset: 10px; }
  .flight-booking-card__route { gap: 8px; }
}

.flight-booking-card__flight--vertical {
  grid-template-columns: 112px minmax(0, 1fr) 144px;
  align-items: start;
  padding: 18px 0;

  .flight-booking-card__route { position: relative; display: flex; flex-direction: column; align-items: stretch; gap: 14px; padding-left: 18px; }
  .flight-booking-card__flight-main { display: grid; gap: 14px; }
  .flight-booking-card__endpoint { grid-template-columns: 52px minmax(0, 1fr); align-items: baseline; }
  .flight-booking-card__time { text-align: left; }
  .flight-booking-card__airport { grid-row: auto; text-align: left; }
  .flight-booking-card__endpoint:first-child .flight-booking-card__airport { text-align: left; }
  .flight-booking-card__connector {
    position: absolute;
    left: 3px;
    top: 10px;
    bottom: 10px;
    width: 1px;
    height: auto;
    align-self: stretch;
    &::before, &::after { left: -3px; right: auto; }
    &::before { top: -3px; }
    &::after { top: auto; bottom: -3px; }
  }
}
.flight-booking-card__flight-info { min-width: 0; }
.flight-booking-card__flight--without-aircraft { grid-template-columns: 112px minmax(0, 1fr); }
.flight-booking-card__flight-meta {
  display: grid;
  gap: 10px;
  margin: 12px 0 0;
  dt { color: var(--booking-muted, #62685d); font-size: 12px; }
  dd { margin: 2px 0 0; font-size: 14px; font-weight: 500; overflow-wrap: anywhere; }
}
.flight-booking-card__duration { white-space: nowrap; font-variant-numeric: tabular-nums; }
.flight-booking-card__via { display: grid; grid-template-columns: 52px minmax(0, 1fr); gap: 12px; color: var(--booking-muted, #62685d); font-size: 14px; overflow-wrap: anywhere; }
.flight-booking-card__baggage {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 7px;
  padding-left: 18px;
  color: var(--booking-muted, #62685d);
  font-size: 13px;
  > span { display: inline-flex; align-items: baseline; gap: 4px; white-space: nowrap; }
  strong { color: var(--booking-ink, #20241d); font-weight: 500; font-variant-numeric: tabular-nums; }
  .pi { font-size: 14px; }
}
.flight-booking-card__aircraft { display: grid; align-content: start; gap: 8px; padding-left: 12px; border-left: 1px solid var(--booking-line, #dce0d6); min-width: 0; }
.flight-booking-card__aircraft-label { color: var(--booking-muted, #62685d); font-size: 12px; }
.flight-booking-card__aircraft-image-slot {
  display: grid;
  place-items: center;
  width: 130px;
  max-width: 100%;
  height: 60px;
  img { display: block; width: 100%; height: 100%; object-fit: contain; }
}
.flight-booking-card__aircraft-fallback { width: 32px; height: 32px; background: url('~@/assets/img/flight-icon.svg') center / contain no-repeat; }
.flight-booking-card__aircraft-name { font-size: 14px; overflow-wrap: anywhere; }
@container booking-itinerary (max-width: 560px) {
  .flight-booking-card__flight--vertical {
    grid-template-columns: 112px minmax(0, 1fr);
    gap: 12px;
    .flight-booking-card__aircraft { grid-column: 2; grid-template-columns: 100px minmax(0, 1fr); align-items: center; padding-left: 18px; border-left: 0; }
    .flight-booking-card__aircraft-label { grid-column: 1 / -1; }
    .flight-booking-card__aircraft-image-slot { width: 100px; }
  }
}
@container booking-itinerary (max-width: 360px) {
  .flight-booking-card__flight--vertical {
    grid-template-columns: minmax(0, 1fr);
    .flight-booking-card__aircraft { grid-column: 1; }
  }
}
</style>
